// HELPERS-2 (Andrei, 2026-10-30): a helper (AG) can be used by several DEs and shows under each
// of them; its "reports to" stays its home. A manager (or whoever may change the helper) adds a
// DE's use from the DE's Helpers tab ("Use a helper", a modal) and removes it with a hover icon.
// A DE declares its own uses through MCP (`declare_helper`); the history seeded the rest.
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldLabel,
  InfoTip,
  NativeSelect,
  NativeSelectOption,
  toast,
  workerName,
} from "@companyos/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { LinkIcon, UnlinkIcon } from "lucide-react";
import { type FormEvent, useId, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { problemText } from "../ops-parts";
import { keys } from "../queries";
import type { Helper } from "./helpers";

/** "Shared" helpers: those this DE uses but does not lead. */
export function isBorrowed(a: Pick<Helper, "reports_to_membership_id">, deId: string): boolean {
  return a.reports_to_membership_id !== deId;
}

/** The candidates "Use a helper" offers: live, not already under the DE, changeable by the viewer. */
export function candidatesFor(all: readonly Helper[], deId: string): Helper[] {
  return all
    .filter(
      (a) =>
        a.can_edit &&
        (a.status === "active" || a.status === "paused") &&
        a.reports_to_membership_id !== deId &&
        !(a.used_by ?? []).some((u) => u.membership_id === deId),
    )
    .sort((x, y) => x.name.localeCompare(y.name));
}

function useSetUse(api: Api, company: CompanyAccess) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { agentId: string; deId: string; used: boolean }) => {
      const params = { co: company.id, id: v.agentId, membership_id: v.deId };
      return v.used
        ? api.client.workforceAgents.addUse({ params })
        : api.client.workforceAgents.removeUse({ params });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.workforceAgents(company.id) }),
    onError: (e) => toast.add({ title: problemText(e, "Not changed."), type: "error" }),
  });
}

/** The hover icon on a shared helper's card: this DE stops using it (its home keeps it). */
export function StopUsingButton({
  api,
  company,
  helper,
  de,
}: {
  api: Api;
  company: CompanyAccess;
  helper: Helper;
  de: { membership_id: string; display_name: string };
}) {
  const set = useSetUse(api, company);
  if (!helper.can_edit) return null;
  const label = `${workerName(de.display_name)} stops using ${workerName(helper.name)}`;
  return (
    <Button
      variant="ghost"
      size="icon-xs"
      aria-label={label}
      title="Stop using"
      data-slot="helper-stop-using"
      className="text-faint hover:text-foreground"
      disabled={set.isPending}
      onClick={(e) => {
        e.preventDefault();
        set.mutate(
          { agentId: helper.id, deId: de.membership_id, used: false },
          { onSuccess: () => toast.add({ title: "Removed from this DE", type: "success" }) },
        );
      }}
    >
      <UnlinkIcon />
    </Button>
  );
}

/** "Use a helper": a modal to pick one of the company's helpers this DE should use too. */
export function UseHelperButton({
  api,
  company,
  de,
  all,
}: {
  api: Api;
  company: CompanyAccess;
  de: { membership_id: string; display_name: string };
  all: readonly Helper[];
}) {
  const [open, setOpen] = useState(false);
  const candidates = candidatesFor(all, de.membership_id);
  if (!candidates.length) return null;
  return (
    <>
      <Button
        variant="ghost"
        size="icon-xs"
        aria-label="Use a helper"
        title="Use a helper"
        data-slot="use-helper"
        className="text-faint hover:text-foreground"
        onClick={() => setOpen(true)}
      >
        <LinkIcon />
      </Button>
      {open ? (
        <UseHelperDialog
          api={api}
          company={company}
          de={de}
          candidates={candidates}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}

function UseHelperDialog({
  api,
  company,
  de,
  candidates,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  de: { membership_id: string; display_name: string };
  candidates: readonly Helper[];
  onClose: () => void;
}) {
  const id = useId();
  const [pick, setPick] = useState(candidates[0]?.id ?? "");
  const set = useSetUse(api, company);
  const chosen = candidates.find((a) => a.id === pick);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!pick) return;
    set.mutate(
      { agentId: pick, deId: de.membership_id, used: true },
      {
        onSuccess: () => {
          toast.add({ title: "Helper added to this DE", type: "success" });
          onClose();
        },
      },
    );
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-slot="use-helper-dialog">
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-1">
              Use a helper
              <InfoTip label="About shared helpers">
                The helper shows under {workerName(de.display_name)} too. It still reports to its
                home; nothing else changes.
              </InfoTip>
            </DialogTitle>
            <DialogDescription>For {workerName(de.display_name)}</DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor={`${id}-agent`}>Helper</FieldLabel>
            <NativeSelect id={`${id}-agent`} value={pick} onChange={(e) => setPick(e.target.value)}>
              {candidates.map((a) => (
                <NativeSelectOption key={a.id} value={a.id}>
                  {workerName(a.name)} · {workerName(a.reports_to.name)}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Field>
          {chosen?.role ? (
            <p className="m-0 line-clamp-2 text-xs text-muted-foreground">{chosen.role}</p>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!pick || set.isPending}>
              {set.isPending ? "Adding…" : "Add"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
